// React/frontend/src/ModelPicker.js
import React, { useState, useMemo, useEffect } from 'react';
import './styles/ModelPicker.css';

/**
 * ModelPicker — searchable, category-grouped SAI model catalog picker.
 * Renders as a fixed overlay above the page (never affects document height,
 * so it can never cause a window scrollbar).
 *
 * Batch add: clicking a code chip toggles that SKU selected (black and white
 * of the same model can both be selected). The footer "+ Add N Models"
 * button commits every selection at once via onAddMany(selections[]).
 */
function ModelPicker({ models, existingKeys = [], onAddMany, onClose }) {
  const [search, setSearch] = useState('');
  // `${modelId}|${bezel}` -> { modelId, name, bezel, modelCode }
  const [selected, setSelected] = useState({});

  // Escape closes the overlay
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const existing = useMemo(() => new Set(existingKeys), [existingKeys]);

  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = (models || []).filter(m =>
      !q ||
      m.name.toLowerCase().includes(q) ||
      (m.blackCode || '').toLowerCase().includes(q) ||
      (m.whiteCode || '').toLowerCase().includes(q) ||
      (m.redCode || '').toLowerCase().includes(q) ||
      m.category.toLowerCase().includes(q)
    );
    const out = [];
    const idx = {};
    filtered.forEach(m => {
      if (idx[m.category] === undefined) {
        idx[m.category] = out.length;
        out.push({ category: m.category, categoryDesc: m.categoryDesc, models: [] });
      }
      out[idx[m.category]].models.push(m);
    });
    return out;
  }, [models, search]);

  const toggle = (m, bezel) => {
    const key = `${m._id}|${bezel}`;
    setSelected(prev => {
      const next = { ...prev };
      if (next[key]) {
        delete next[key];
      } else {
        next[key] = {
          modelId: m._id,
          name: m.name,
          bezel,
          modelCode: bezel === 'white' ? m.whiteCode : bezel === 'red' ? m.redCode : m.blackCode
        };
      }
      return next;
    });
  };

  const count = Object.keys(selected).length;

  const handleAddSelected = () => {
    if (!count) return;
    onAddMany(Object.values(selected));
  };

  // One selectable SKU chip (bezel = 'black' | 'white' | null for accessories)
  const renderChip = (m, bezel, code, label, extraClass) => {
    const added = existing.has(`${m.name}|${bezel}`);
    const isSel = !!selected[`${m._id}|${bezel}`];
    return (
      <button
        type="button"
        className={`mp-chip ${extraClass} ${isSel ? 'mp-chip-active' : ''} ${added ? 'mp-chip-added' : ''}`}
        disabled={added}
        title={added ? 'Already added' : (isSel ? 'Click to unselect' : 'Click to select')}
        onClick={() => toggle(m, bezel)}
      >
        {(added || isSel) && <span className="mp-chip-check">✓ </span>}
        {label ? `${label} · ` : ''}{code}
      </button>
    );
  };

  return (
    <div className="mp-overlay" onClick={onClose}>
    <div className="mp-container" onClick={(e) => e.stopPropagation()}>
      <div className="mp-search-row">
        <input
          type="text"
          className="mp-search"
          placeholder="Search model, size, or code..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
        />
      </div>
      <div className="mp-results">
        {groups.length === 0 && (
          <div className="mp-empty">
            {models?.length ? 'No models match your search.' : 'No models — check server seed.'}
          </div>
        )}
        {groups.map(g => (
          <div key={g.category} className="mp-group">
            <div className="mp-group-header">
              <span className="mp-group-name">{g.category}</span>
              {g.categoryDesc && <span className="mp-group-desc">{g.categoryDesc}</span>}
            </div>
            {g.models.map(m => (
              <div key={m._id} className="mp-model-row">
                <span className="mp-model-name">{m.name}</span>
                <span className="mp-bezel-chips">
                  {m.isAccessory ? (
                    renderChip(m, null, m.blackCode, '', 'mp-chip-code')
                  ) : (
                    <>
                      {m.blackCode && renderChip(m, 'black', m.blackCode, 'Black', 'mp-chip-black')}
                      {m.whiteCode && renderChip(m, 'white', m.whiteCode, 'White', 'mp-chip-white')}
                      {m.redCode && renderChip(m, 'red', m.redCode, 'Red', 'mp-chip-red')}
                    </>
                  )}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="mp-footer">
        <span className="mp-count">
          {count > 0 ? `${count} selected` : 'Click model codes to select'}
        </span>
        <button type="button" className="srf-btn srf-btn-secondary" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="srf-btn srf-btn-primary"
          disabled={!count}
          onClick={handleAddSelected}
        >
          + Add {count > 0 ? `${count} ` : ''}{count === 1 ? 'Model' : 'Models'}
        </button>
      </div>
    </div>
    </div>
  );
}

export default ModelPicker;
